<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import MediaViewer from "@/components/general-search/media-viewer.vue";
import moment from "moment";
import Swal from "sweetalert2";
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { adjudicationService } from "@/helpers/authservice/adjudication.service";
import {
  appealStatusLabel,
  appealStatusVariant,
  appealSlotLabel,
} from "@/helpers/constants";

// Plain text, deliberately. A reply reaches the appellant through their portal
// and through the notification email, and neither renders markup: the portal
// prints the body with `white-space: pre-wrap` and the email escapes it through
// Blade, so the rich text editor that used to sit here showed the citizen a
// literal "<p>Message Here</p>". The toolbar was already cut down to what a
// plain-text fallback could represent, so nothing of value is lost by dropping
// it — and the body is now one content type across all three channels.
const REPLY_MAX = 5000;

const store = useStore();
const route = useRoute();
const router = useRouter();

const loading = computed(() => store.state.globals.loading);
const ticket = computed(() => store.state.adjudication.ticket);
const liftReasons = computed(() => store.state.adjudication.lift_reasons || []);

const id = route.params.id;

const title = ref("Adjudication - Contest");
const items = ref([
  { text: "Home", href: "/" },
  { text: "Adjudication", href: "/adjudication/queue" },
  { text: "Contest", active: true },
]);

/* --------------------------------------------------------------- form state */

const reply = ref({ body: "", request_documents: false, files: [] });
const hearing = ref({ hearing_at: "", hearing_venue: "", hearing_remarks: "" });

const amend = ref({
  open: false,
  appeal_id: null,
  nov_no: "",
  amount: "",
  current_amount: "",
  attachment: null,
});
const voiding = ref({
  open: false,
  appeal_id: null,
  traffic_record_id: null,
  nov_no: "",
  note: "",
  lift_reason_id: "",
  attachment: null,
});
const denying = ref({ open: false, appeal_id: null, nov_no: "", reason: "" });

const busy = ref(false);

/* ------------------------------------------------------------------ helpers */

const formatted = (date) => (date ? moment(date).format("YYYY-MM-DD h:mm A") : "—");

// The `min` for the hearing picker, in the format datetime-local expects. Local
// time deliberately: the field is local, so a UTC bound would be off by the
// offset and either block valid slots or admit past ones. Start of tomorrow,
// not "now" — a hearing can't be scheduled for later today, since the
// appellant needs at least a day's notice to appear.
const earliestHearing = computed(() =>
  moment().add(1, "day").startOf("day").format("YYYY-MM-DDTHH:mm")
);

// A scheduled hearing has one date that matters more than anything else on
// this card — broken out so it can be read at a glance, the way a printed
// hearing notice puts the date on its own stub rather than in a sentence.
const hearingCalendar = computed(() => {
  if (!ticket.value || !ticket.value.hearing) return null;

  const at = moment(ticket.value.hearing.at);

  return {
    month: at.format("MMM").toUpperCase(),
    day: at.format("D"),
    weekday: at.format("ddd"),
    time: at.format("h:mm A"),
  };
});

const peso = (value) =>
  value === null || value === undefined
    ? "—"
    : `₱${Number(value).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const appellantMessages = computed(() =>
  ticket.value ? ticket.value.messages : []
);

const dayLabel = (date) => {
  const day = moment(date).startOf("day");
  const diff = moment().startOf("day").diff(day, "days");

  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";

  return day.format("MMMM D, YYYY");
};

// A day divider ahead of the first message of each new day, so a thread that
// spans a request-for-documents back-and-forth over multiple sittings can be
// scanned instead of read start to finish.
const messageThread = computed(() => {
  let lastDay = null;

  return appellantMessages.value.map((message) => {
    const day = moment(message.created_at).format("YYYY-MM-DD");
    const showDivider = day !== lastDay;
    lastDay = day;

    return { message, showDivider, dividerLabel: dayLabel(message.created_at) };
  });
});

const appellantFiles = computed(() =>
  ticket.value
    ? ticket.value.attachments.filter((a) => a.uploaded_by === "appellant")
    : []
);

const officeFiles = computed(() =>
  ticket.value
    ? ticket.value.attachments.filter((a) => a.uploaded_by === "adjudicator")
    : []
);

const isOpen = computed(() => !!ticket.value && ticket.value.is_open);

const load = () => store.dispatch("adjudication/getTicket", id).catch(() => {
  // A 403 here means the ticket belongs to another LGU. The queue is the only
  // sensible place to land.
  router.push({ name: "adjudication-queue" });
});

const failure = (error) => {
  const data = (error && error.response && error.response.data) || {};
  const message = data.errors
    ? Object.values(data.errors).flat().join("\n")
    : data.message || "Something went wrong. Please try again.";

  Swal.fire("Failed", message, "error");
};

/* ------------------------------------------------------------------ actions */

// One action, not two. A document sent on its own gave the citizen a file with
// no explanation; an explanation sent on its own referred to a file that landed
// as a separate entry. Attachments are optional — a plain message still sends.
const submitReply = async () => {
  if (!reply.value.body.trim()) return;

  try {
    await store.dispatch("adjudication/sendMessage", {
      id,
      body: reply.value.body,
      request_documents: reply.value.request_documents,
      files: reply.value.files,
    });

    reply.value = { body: "", request_documents: false, files: [] };

    const picker = document.getElementById("reply-files");
    if (picker) picker.value = "";
    window.location.reload();
  } catch (error) {
    console.log(error);
    // The module already surfaced it through the notification bucket.
  }
};

const onFilesPicked = (event) => {
  reply.value.files = Array.from(event.target.files || []);
};

const submitHearing = async () => {
  // Belt and suspenders on top of the picker's `min`: a browser that lets a
  // date be typed in rather than picked doesn't necessarily enforce it.
  if (moment(hearing.value.hearing_at).isBefore(earliestHearing.value)) {
    await Swal.fire({
      title: "Give the appellant a day's notice",
      text: "The earliest a hearing can be scheduled is tomorrow — pick a later date and time.",
      icon: "warning",
    });

    return;
  }

  try {
    await store.dispatch("adjudication/setHearing", { id, ...hearing.value });
    hearing.value = { hearing_at: "", hearing_venue: "", hearing_remarks: "" };
    window.location.reload();
  } catch (error) {
    console.log(error);
    // Already surfaced.
  }
};

// The reason is required, not optional: the citizen may have taken leave and
// travelled for this, so "cancelled" with no explanation is not good enough.
const cancellingHearing = ref({ open: false, reason: "" });

const openCancelHearing = () => {
  cancellingHearing.value = { open: true, reason: "" };
};

const submitCancelHearing = async () => {
  if (!cancellingHearing.value.reason.trim()) return;

  cancellingHearing.value.open = false;

  try {
    await store.dispatch("adjudication/setHearing", {
      id,
      hearing_at: null,
      hearing_venue: null,
      hearing_remarks: null,
      cancel_remarks: cancellingHearing.value.reason,
    });
    window.location.reload();
  } catch (error) {
    // Already surfaced.
  }
};

/* ------------------------------------------- the existing amend/void actions */

const openAmend = (appeal) => {
  const current = appeal.record ? appeal.record.current_amount : "";

  amend.value = {
    open: true,
    appeal_id: appeal.id,
    nov_no: appeal.nov_no,
    amount: current,
    // Kept so submitAmend can tell whether the figure was actually changed.
    current_amount: current,
    attachment: null,
  };
};

// Shown live under the amount field, so the direction and size of the change
// is visible before confirming rather than only in the confirmation dialog.
const amendDelta = computed(() => {
  if (amend.value.amount === "" || amend.value.current_amount === "") return null;

  const change = Number(amend.value.amount) - Number(amend.value.current_amount);

  return { change, up: change > 0 };
});

const submitAmend = async () => {
  // Required, not a UI preference: changeAmount does all of its work inside a
  // loop over the attachments, so with no file it never runs and errors out.
  if (!amend.value.attachment || !amend.value.amount) return;

  // Amending to the figure it already holds writes an amendment, mails the
  // appellant a "new amount" identical to the old one, and burns this NOV's one
  // and only outcome. Almost always a misread of the form, so it is stopped
  // rather than confirmed.
  if (Number(amend.value.amount) === Number(amend.value.current_amount)) {
    await Swal.fire({
      title: "The amount has not changed",
      html: `${peso(amend.value.current_amount)} is already the amount on `
        + `${amend.value.nov_no}. Enter the new amount, or void the record `
        + `instead if the notice should be cancelled.`,
      icon: "warning",
    });

    return;
  }

  const confirmed = await Swal.fire({
    title: `Amend ${amend.value.nov_no}?`,
    html: `The penalty changes from <strong>${peso(amend.value.current_amount)}</strong> `
      + `to <strong>${peso(amend.value.amount)}</strong>. This updates the traffic `
      + `record, tells the appellant, and cannot be undone or changed afterwards.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: "#34c38f",
    confirmButtonText: "Amend the amount",
  });

  if (!confirmed.value) return;

  busy.value = true;

  try {
    await adjudicationService.amendAmount({
      nov_number: amend.value.nov_no,
      amount: amend.value.amount,
      attachment: amend.value.attachment,
    });

    amend.value.open = false;
    await load();
    await setAppealStatus(
      amend.value.appeal_id,
      "amended",
      `Amount amended to ${amend.value.amount}.`,
      `Amended ${amend.value.nov_no}.`
    );
    window.location.reload();
  } catch (error) {
    failure(error);
  } finally {
    busy.value = false;
  }
};

const openVoid = (appeal) => {
  voiding.value = {
    open: true,
    appeal_id: appeal.id,
    traffic_record_id: appeal.traffic_record_id,
    nov_no: appeal.nov_no,
    note: "",
    lift_reason_id: "",
    attachment: null,
  };
};

const submitVoid = async () => {
  if (
    !voiding.value.attachment ||
    !voiding.value.note ||
    !voiding.value.lift_reason_id
  ) {
    return;
  }

  const confirmed = await Swal.fire({
    title: `Void the record for ${voiding.value.nov_no}?`,
    html: "The notice is cancelled, nothing more is due on it, and it stops "
      + "appearing in the appellant's list of notices. This lifts the LTO alarm "
      + "and cannot be undone or changed afterwards.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f46a6a",
    confirmButtonText: "Void the record",
  });

  if (!confirmed.value) return;

  busy.value = true;

  try {
    await adjudicationService.voidRecord({
      traffic_record_id: voiding.value.traffic_record_id,
      note: voiding.value.note,
      lift_reason_id: voiding.value.lift_reason_id,
      attachment: voiding.value.attachment,
    });

    voiding.value.open = false;
    await load();
    await setAppealStatus(
      voiding.value.appeal_id,
      "voided",
      voiding.value.note,
      `Voided the record for ${voiding.value.nov_no}.`
    );
    window.location.reload();
  } catch (error) {
    failure(error);
  } finally {
    busy.value = false;
  }
};

/**
 * Record how one contested NOV ended.
 *
 * The amend and void endpoints know nothing about contests, so the outcome has
 * to be written here after one succeeds. The server decides what that means for
 * the contest as a whole — it closes the ticket once no notice is left
 * undecided, as `resolved` unless every notice was denied — so this no longer
 * asks the adjudicator whether to close anything.
 */
const setAppealStatus = async (appealId, status, notes, done) => {
  try {
    await store.dispatch("adjudication/setAppealStatus", {
      id,
      appealId,
      status,
      notes,
    });

    const closed = ticket.value && !ticket.value.is_open;

    await Swal.fire({
      title: done,
      text: closed
        ? "Every contested NOV has now been decided, so the contest is resolved "
          + "and the appellant has been told."
        : "Recorded against this NOV. The contest stays open until every NOV is "
          + "decided.",
      icon: "success",
      timer: 4000,
    });
  } catch (error) {
    // The amend or void itself already happened; say so rather than letting the
    // adjudicator think nothing was recorded.
    failure(error);
  }
};

// Denying a NOV changes no traffic record — it only records that the contest
// did not succeed for it — so the reason is asked for here and nowhere else.
const openDeny = (appeal) => {
  denying.value = { open: true, appeal_id: appeal.id, nov_no: appeal.nov_no, reason: "" };
};

const submitDeny = async () => {
  if (!denying.value.reason.trim()) return;

  denying.value.open = false;

  await setAppealStatus(
    denying.value.appeal_id,
    "denied",
    denying.value.reason,
    `Denied ${denying.value.nov_no}.`
  );
};

// Named for the NOV, not the ticket: appealStatusLabel/appealStatusVariant are
// already imported from the shared constants and label the TICKET's status.
const novStatusLabel = (status) =>
  ({
    amended: "Amount amended",
    voided: "Record voided",
    denied: "Denied",
  }[status] || status);

// "dark" is deliberately avoided here: this theme's dark mode treats
// --vz-dark/--vz-light as relative surface tokens and swaps them (dark
// becomes near-white, light becomes near-black), which is fine for a
// background but breaks anything — like this badge, via BBadge's built-in
// white text — that pairs "dark" with a fixed light text color. "secondary"
// is a real, theme-constant color and stays legible in both themes.
const novStatusVariant = (status) =>
  ({ amended: "warning", voided: "secondary", denied: "danger" }[status] || "secondary");

const novStatusIcon = (status) =>
  ({
    amended: "ri-pencil-line",
    voided: "ri-forbid-line",
    denied: "ri-close-circle-line",
  }[status] || "ri-time-line");

// Purely cosmetic — initials for the small avatar circles next to a name.
const initials = (name) => {
  if (!name) return "?";

  const parts = name.trim().split(/\s+/);
  const letters = parts.length > 1 ? [parts[0], parts[parts.length - 1]] : [parts[0]];

  return letters.map((p) => p[0].toUpperCase()).join("");
};

const fileIcon = (filename) => {
  const ext = (filename || "").split(".").pop().toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "ri-image-2-line";
  if (ext === "pdf") return "ri-file-pdf-2-line";
  return "ri-file-3-line";
};

const isImageFile = (filename) => fileIcon(filename) === "ri-image-2-line";

// A signed URL that 404s or expires still leaves the <img> tag in the DOM
// showing a broken-image glyph — tracked here so it falls back to the file
// icon instead, the same as a file with no temporary_url at all.
const brokenThumbnails = ref(new Set());
const markThumbnailBroken = (fileId) => {
  brokenThumbnails.value = new Set(brokenThumbnails.value).add(fileId);
};

onMounted(() => {
  load();
  store.dispatch("adjudication/getLiftReasons");
});
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-overlay :show="loading">
      <div v-if="ticket">
        <!-- header -->
        <div
          class="card mb-3 border-0 border-start border-4"
          :class="`border-${appealStatusVariant(ticket.status)}`"
        >
          <div class="card-body py-3">
            <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <h1 class="fs-4 mb-0">{{ ticket.ticket_number }}</h1>
                  <b-badge :variant="appealStatusVariant(ticket.status)" pill class="px-2 py-1">
                    {{ appealStatusLabel(ticket.status) }}
                  </b-badge>
                </div>
                <div class="d-flex flex-wrap column-gap-3 row-gap-1 meta-text mt-2">
                  <span><i class="ri-calendar-line align-bottom me-1"></i>Filed {{ formatted(ticket.filed_at) }}</span>
                  <span v-if="ticket.lgu">
                    &nbsp;<i class="ri-government-line align-bottom me-1"></i>{{ ticket.lgu.name }}
                  </span>
                  <span v-if="ticket.adjudicator">
                    &nbsp;<i class="ri-user-settings-line align-bottom me-1"></i>{{ ticket.adjudicator.name }}
                  </span>
                </div>
              </div>
              <router-link to="/adjudication/queue" class="btn btn-light btn-sm">
                <i class="ri-arrow-left-line align-bottom me-1"></i>Back to contest list
              </router-link>
            </div>

            <div
              v-if="ticket.ack_email_status === 'failed' || ticket.push_pending"
              class="d-flex flex-column gap-2 mt-3"
            >
              <div
                v-if="ticket.ack_email_status === 'failed'"
                class="alert alert-danger d-flex align-items-center gap-2 py-2 mb-0"
                role="alert"
              >
                <i class="ri-mail-close-line fs-16"></i>
                <span>
                  The acknowledgement email failed to send — the citizen may not have
                  their ticket link.
                </span>
              </div>
              <div
                v-if="ticket.push_pending"
                class="alert alert-warning d-flex align-items-center gap-2 py-2 mb-0"
                role="alert"
                :title="ticket.push_error || ''"
              >
                <i class="ri-wifi-off-line fs-16"></i>
                <span>Processing to the citizen's portal.</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- 1. the contest -->
          <div class="col-md-6 col-xl-4">
            <b-card class="mb-3">
              <h6 class="text-muted text-uppercase small fw-semibold mb-2">
                <i class="ri-user-line align-bottom me-1"></i>Appellant
              </h6>

              <div v-if="ticket.appellant">
                <div class="d-flex align-items-center gap-2 mb-3">
                  <div class="avatar-xs flex-shrink-0">
                    <span class="avatar-title bg-soft-primary text-primary rounded-circle fw-semibold">
                      {{ initials(ticket.appellant.full_name) }}
                    </span>
                  </div>
                  <div>
                    <p class="mb-0 fw-semibold fs-15">{{ ticket.appellant.full_name }}</p>
                    <p class="mb-0 meta-text">
                      <i class="ri-phone-line align-bottom me-1"></i>{{ ticket.appellant.contact_number || "—" }}
                    </p>
                    <p v-if="ticket.appellant.email" class="mb-0 meta-text">
                      <i class="ri-mail-line align-bottom me-1"></i>{{ ticket.appellant.email }}
                    </p>
                  </div>
                </div>
                <p class="mb-3 meta-text">
                  <i class="ri-map-pin-line align-bottom me-1"></i>
                  {{ ticket.appellant.houseno }}, {{ ticket.appellant.brgy }},
                  {{ ticket.appellant.city }}, {{ ticket.appellant.province }}
                  {{ ticket.appellant.zipcode }}
                </p>
                <hr class="mt-0" />
                <dl class="row mb-0 gy-2">
                  <dt class="col-6 text-muted small fw-normal text-uppercase">Registered Owner</dt>
                  <dd class="col-6 mb-0">{{ ticket.appellant.registered_owner || "—" }}</dd>
                  <dt class="col-6 text-muted small fw-normal text-uppercase">Name of Driver</dt>
                  <dd class="col-6 mb-0">{{ ticket.appellant.driver_name || "—" }}</dd>
                  <dt class="col-6 text-muted small fw-normal text-uppercase">License Number</dt>
                  <dd class="col-6 mb-0">{{ ticket.appellant.license_number || "—" }}</dd>
                  <dt class="col-6 text-muted small fw-normal text-uppercase">Type of Complainant</dt>
                  <dd class="col-6 mb-0">{{ ticket.appellant.client_type || "—" }}</dd>
                  <dt class="col-6 text-muted small fw-normal text-uppercase">Type of Vehicle</dt>
                  <dd class="col-6 mb-0">{{ ticket.appellant.vehicle_type || "—" }}</dd>
                  <dt class="col-6 text-muted small fw-normal text-uppercase">Agency</dt>
                  <dd class="col-6 mb-0">{{ ticket.appellant.agency || "—" }}</dd>
                </dl>
              </div>
              <p v-else class="text-muted mb-0">No appellant details were supplied.</p>
            </b-card>

            <b-card class="mb-3">
              <h6 class="text-muted text-uppercase small fw-semibold mb-2">
                <i class="ri-file-text-line align-bottom me-1"></i>Brief Narration of Facts
              </h6>
              <div class="contest-narration mb-3">
                {{ ticket.narration }}
              </div>
            </b-card>

            <b-card class="mb-3">
              <h6 class="text-muted text-uppercase small fw-semibold mb-2">
                <i class="ri-inbox-archive-line align-bottom me-1"></i>Documents from the appellant
              </h6>
              <p v-if="!appellantFiles.length" class="meta-text mb-0">
                No documents.
              </p>
              <ul v-else class="list-unstyled mb-0 d-flex flex-column gap-2">
                <li v-for="file in appellantFiles" :key="file.id">
                  <component
                    :is="file.temporary_url ? 'a' : 'div'"
                    :href="file.temporary_url || undefined"
                    :target="file.temporary_url ? '_blank' : undefined"
                    :rel="file.temporary_url ? 'noopener' : undefined"
                    class="doc-row"
                    :class="{ 'doc-row-disabled': !file.temporary_url }"
                  >
                    <div class="doc-thumb flex-shrink-0">
                      <img
                        v-if="isImageFile(file.original_name) && file.temporary_url && !brokenThumbnails.has(file.id)"
                        :src="file.temporary_url"
                        :alt="file.original_name"
                        loading="lazy"
                        @error="markThumbnailBroken(file.id)"
                      />
                      <i v-else :class="fileIcon(file.original_name)"></i>
                    </div>
                    <div class="flex-grow-1 overflow-hidden">
                      <span v-if="appealSlotLabel(file.slot)" class="doc-slot">
                        {{ appealSlotLabel(file.slot) }}
                      </span>
                      <div class="text-truncate" :class="{ 'text-danger': !file.temporary_url }">
                        {{ file.original_name }}
                      </div>
                      <div class="d-flex align-items-center gap-2 mt-1">
                        <span class="meta-text">{{ formatted(file.created_at) }}</span>
                        <b-badge v-if="!file.temporary_url" variant="danger">Unavailable</b-badge>
                      </div>
                    </div>
                  </component>
                </li>
              </ul>
            </b-card>

            <!-- Read-only now: documents are sent with the message that
                 explains them, from the Messages card above. -->
            <b-card class="mb-3">
              <h6 class="text-muted text-uppercase small fw-semibold mb-2">
                <i class="ri-inbox-unarchive-line align-bottom me-1"></i>Documents from the office
              </h6>
              <ul v-if="officeFiles.length" class="list-unstyled mb-0 d-flex flex-column gap-2">
                <li v-for="file in officeFiles" :key="file.id">
                  <component
                    :is="file.temporary_url ? 'a' : 'div'"
                    :href="file.temporary_url || undefined"
                    :target="file.temporary_url ? '_blank' : undefined"
                    :rel="file.temporary_url ? 'noopener' : undefined"
                    class="doc-row"
                    :class="{ 'doc-row-disabled': !file.temporary_url }"
                  >
                    <div class="doc-thumb flex-shrink-0">
                      <img
                        v-if="isImageFile(file.original_name) && file.temporary_url && !brokenThumbnails.has(file.id)"
                        :src="file.temporary_url"
                        :alt="file.original_name"
                        loading="lazy"
                        @error="markThumbnailBroken(file.id)"
                      />
                      <i v-else :class="fileIcon(file.original_name)"></i>
                    </div>
                    <div class="flex-grow-1 overflow-hidden">
                      <div class="text-truncate" :class="{ 'text-danger': !file.temporary_url }">
                        {{ file.original_name }}
                      </div>
                      <div class="d-flex align-items-center gap-2 mt-1">
                        <b-badge v-if="!file.temporary_url" variant="danger">Unavailable</b-badge>
                        <b-badge v-else-if="!file.pushed_at" variant="warning">
                          Processing
                        </b-badge>
                      </div>
                    </div>
                  </component>
                </li>
              </ul>
              <p v-else class="meta-text mb-0">
                No documents. Attach them to a message above to send one.
              </p>
            </b-card>
          </div>

          <!-- 2. the records -->
          <div class="col-md-6 col-xl-4">
            <div
              v-for="appeal in ticket.appeals"
              :key="appeal.id"
              class="card mb-3 nov-card border-start border-4"
              :class="
                `border-${appeal.is_decided ? novStatusVariant(appeal.status) : 'primary'}`
              "
            >
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start flex-wrap mb-2 gap-2">
                  <h5 class="card-title mb-0">{{ appeal.nov_no }}</h5>
                  <b-badge
                    :variant="appeal.is_decided ? novStatusVariant(appeal.status) : 'primary'"
                    pill
                  >
                    <i
                      :class="appeal.is_decided ? novStatusIcon(appeal.status) : 'ri-time-line'"
                      class="align-bottom me-1"
                    ></i>
                    {{ appeal.is_decided ? novStatusLabel(appeal.status) : "Awaiting decision" }}
                  </b-badge>
                </div>

                <p v-if="!appeal.record" class="text-warning mb-0">
                  <i class="ri-error-warning-line align-bottom me-1"></i>
                  This NOV does not match any record in TCMS. It may have been
                  archived after payment or a void. Nothing can be amended from here.
                </p>

                <div v-else>
                  <div class="row g-2 mb-2">
                    <div class="col-6">
                      <div class="text-muted small">Violation date</div>
                      <div>{{ formatted(appeal.violation_date) }}</div>
                    </div>
                    <div class="col-6">
                      <div class="text-muted small">Amount when filed</div>
                      <div>{{ peso(appeal.amount_at_filing) }}</div>
                    </div>
                    <div class="col-6">
                      <div class="text-muted small">Amount now</div>
                      <div class="fw-semibold">{{ peso(appeal.record.current_amount) }}</div>
                    </div>
                    <div
                      v-if="
                        appeal.record.current_amount &&
                        Number(appeal.record.current_amount) >
                          Number(appeal.amount_at_filing)
                      "
                      class="col-6 d-flex align-items-end"
                    >
                      <span
                        class="badge bg-soft-danger text-danger fw-normal"
                        title="Interest keeps accruing while a contest is pending"
                      >
                        <i class="ri-arrow-up-line align-bottom"></i> grown since filing
                      </span>
                    </div>
                  </div>

                  <ul class="list-unstyled small mb-2 border-top pt-2">
                    <li
                      v-for="(v, i) in appeal.record.violations"
                      :key="i"
                      class="d-flex justify-content-between gap-2 py-1"
                    >
                      <span>{{ v.name }} <span class="text-muted">({{ v.article }})</span></span>
                      <span class="text-nowrap">{{ peso(v.amount) }}</span>
                    </li>
                  </ul>

                  <div class="mb-2 d-flex gap-1 flex-wrap">
                    <b-badge v-if="appeal.record.is_paid" variant="success">Paid</b-badge>
                    <b-badge v-if="appeal.record.is_voided" variant="secondary">
                      Voided
                    </b-badge>
                    <b-badge v-if="appeal.record.is_payment_queue" variant="info">
                      On the payment portal
                    </b-badge>
                  </div>

                  <a
                    v-if="appeal.record.nov_pdf_url"
                    :href="appeal.record.nov_pdf_url"
                    target="_blank"
                    rel="noopener"
                    class="d-block mb-2 small"
                  >
                    <i class="ri-file-pdf-2-line align-bottom me-1"></i>View the NOV PDF
                  </a>

                  <!-- The apprehension itself, in place rather than behind a
                       button: deciding a NOV means looking at what was captured,
                       so it sits next to the notice it belongs to. Same viewer the
                       traffic record screen uses — carousel, thumbnails, panzoom,
                       video — and one per card, which is only safe because each
                       instance now scopes its own video element id. -->
                  <div v-if="appeal.record.media && appeal.record.media.length" class="mb-2">
                    <div class="meta-text mb-1">
                      Evidence ({{ appeal.record.media.length }} file(s))
                    </div>
                    <MediaViewer
                      size="100%"
                      :medias="appeal.record.media"
                      :traffic_record="appeal.record"
                    />
                  </div>

                  <p v-else class="small text-muted mb-2">No media on this record.</p>

                  <!-- Decided: the outcome replaces the actions. Final, so there is
                       nothing left to do to this notice from here. -->
                  <div v-if="appeal.is_decided" class="mt-2 pt-2 border-top">
                    <span class="text-muted small">
                      <i class="ri-check-line align-bottom me-1"></i>
                      Decided {{ formatted(appeal.status_at) }}
                    </span>
                    <p
                      v-if="appeal.status_notes"
                      class="small mb-0 mt-1"
                      style="white-space: pre-wrap"
                    >
                      {{ appeal.status_notes }}
                    </p>
                  </div>

                  <!-- Undecided, on a live contest: one outcome per notice. Gone
                       once the contest closes, and gone for this notice once it has
                       an outcome of its own. Amending or voiding a record for
                       reasons unrelated to a contest is still done from the traffic
                       record screen, where that authority lives. -->
                  <div v-else-if="isOpen" class="d-flex gap-2 flex-wrap pt-2 border-top mt-2">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      type="button"
                      :disabled="!!appeal.record.is_paid || !!appeal.record.is_voided"
                      :title="
                        appeal.record.is_paid
                          ? 'Already paid — amend the amount from the payment screens'
                          : 'Amend the penalty amount'
                      "
                      @click="openAmend(appeal)"
                    >
                      <i class="ri-pencil-line align-bottom me-1"></i>Amend amount
                    </button>
                    <button
                      class="btn btn-sm btn-outline-danger"
                      type="button"
                      :disabled="!!appeal.record.is_voided"
                      @click="openVoid(appeal)"
                    >
                      <i class="ri-forbid-line align-bottom me-1"></i>Void record
                    </button>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      type="button"
                      title="The notice stands as issued"
                      @click="openDeny(appeal)"
                    >
                      <i class="ri-close-line align-bottom me-1"></i>Deny
                    </button>
                  </div>

                  <p v-if="!appeal.is_decided && isOpen" class="text-muted small mt-2 mb-0">
                    Deciding every NOV closes the contest automatically. Changing the
                    violation itself is not done from here — use Edit Violation on
                    the record.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- 3. thread, hearing, decision -->
          <div class="col-md-12 col-xl-4">
            <b-card class="mb-3">
              <h6 class="text-muted text-uppercase small fw-semibold mb-3">
                <i class="ri-calendar-event-line align-bottom me-1"></i>Hearing
              </h6>
              <div v-if="ticket.hearing" class="d-flex gap-3">
                <!-- The one fact that matters most on this card, broken out the
                     way a printed hearing notice puts the date on its own stub
                     rather than folding it into a sentence. -->
                <div class="hearing-date-block flex-shrink-0 text-center">
                  <div class="hearing-date-month">{{ hearingCalendar.month }}</div>
                  <div class="hearing-date-day">{{ hearingCalendar.day }}</div>
                  <div class="hearing-date-weekday">{{ hearingCalendar.weekday }}</div>
                </div>
                <div class="flex-grow-1 overflow-hidden">
                  <p class="mb-1 fw-semibold fs-15">
                    <i class="ri-time-line align-bottom me-1 text-primary"></i>
                    {{ hearingCalendar.time }}
                  </p>
                  <p class="mb-2 meta-text">
                    <i class="ri-map-pin-line align-bottom me-1"></i>{{ ticket.hearing.venue }}
                  </p>
                  <p v-if="ticket.hearing.remarks" class="hearing-remarks mb-2">
                    {{ ticket.hearing.remarks }}
                  </p>
                  <button
                    v-if="isOpen"
                    class="btn btn-outline-danger btn-sm"
                    type="button"
                    @click="openCancelHearing"
                  >
                    <i class="ri-close-circle-line align-bottom me-1"></i>Cancel hearing
                  </button>
                </div>
              </div>

              <div v-else-if="isOpen">
                <div class="mb-2">
                  <label class="form-label small" for="hearing-at">Date and time</label>
                  <div class="input-group input-group-sm">
                    <span class="input-group-text"><i class="ri-calendar-line"></i></span>
                    <!-- min, not just server-side validation: the server rejects a
                         past hearing with `after:now`, and finding that out after
                         filling the form in is worse than not being able to pick it. -->
                    <input
                      id="hearing-at"
                      class="form-control"
                      type="datetime-local"
                      :min="earliestHearing"
                      v-model="hearing.hearing_at"
                    />
                  </div>
                  <p class="meta-text mb-0">Earliest is tomorrow — today isn't enough notice.</p>
                </div>
                <div class="mb-2">
                  <label class="form-label small" for="hearing-venue">Venue</label>
                  <div class="input-group input-group-sm">
                    <span class="input-group-text"><i class="ri-map-pin-line"></i></span>
                    <input
                      id="hearing-venue"
                      class="form-control"
                      type="text"
                      placeholder="Room, floor, building…"
                      v-model="hearing.hearing_venue"
                    />
                  </div>
                </div>
                <div class="mb-2">
                  <label class="form-label small" for="hearing-remarks">
                    Notes for the appellant
                  </label>
                  <div class="input-group input-group-sm">
                    <span class="input-group-text"><i class="ri-sticky-note-line"></i></span>
                    <textarea
                      id="hearing-remarks"
                      class="form-control"
                      rows="2"
                      placeholder="What to bring, who to ask for…"
                      v-model="hearing.hearing_remarks"
                    ></textarea>
                  </div>
                </div>
                <button
                  class="btn btn-primary btn-sm"
                  type="button"
                  :disabled="!hearing.hearing_at || !hearing.hearing_venue"
                  @click="submitHearing"
                >
                  <i class="ri-calendar-check-line align-bottom me-1"></i>Schedule
                </button>
                <p class="meta-text mb-0 mt-2">
                  The appellant sees this on their portal as soon as it's scheduled.
                </p>
              </div>

              <p v-else class="meta-text mb-0">No hearing was scheduled.</p>
            </b-card>

            <b-card class="mb-3">
              <h6 class="text-muted text-uppercase small fw-semibold mb-3">
                <i class="ri-chat-3-line align-bottom me-1"></i>Messages
              </h6>

              <div v-if="!appellantMessages.length" class="text-center py-4 mb-1">
                <div class="avatar-sm mx-auto mb-2">
                  <span class="avatar-title bg-soft-secondary text-secondary rounded-circle fs-20">
                    <i class="ri-chat-3-line"></i>
                  </span>
                </div>
                <p class="meta-text mb-0">No messages yet.</p>
              </div>

              <div v-else class="message-thread mb-1">
                <template v-for="item in messageThread" :key="item.message.id">
                  <div v-if="item.showDivider" class="text-center my-3">
                    <b-badge variant="secondary" pill class="fw-normal px-3 py-1">
                      {{ item.dividerLabel }}
                    </b-badge>
                  </div>

                  <div
                    class="d-flex mb-3 gap-2"
                    :class="item.message.author_type === 'adjudicator' ? 'flex-row-reverse' : ''"
                  >
                    <div class="avatar-xs flex-shrink-0">
                      <span
                        class="avatar-title rounded-circle fw-semibold small"
                        :class="
                          item.message.author_type === 'adjudicator'
                            ? 'bg-soft-primary text-primary'
                            : 'bg-soft-secondary text-secondary'
                        "
                      >
                        {{
                          initials(
                            item.message.author_name ||
                              (item.message.author_type === "adjudicator" ? "Office" : "Appellant")
                          )
                        }}
                      </span>
                    </div>
                    <div
                      class="chat-bubble"
                      :class="item.message.author_type === 'adjudicator' ? 'chat-bubble-mine' : 'chat-bubble-theirs'"
                    >
                      <div class="fw-semibold mb-1">
                        {{
                          item.message.author_type === "adjudicator"
                            ? item.message.author_name || "Adjudication office"
                            : item.message.author_name || "Appellant"
                        }}
                      </div>
                      <!-- Both sides are plain text and neither is trusted with
                           v-html. Office replies used to be rendered as HTML here,
                           which made the workbench the only place a reply looked
                           right — the portal and the email showed the citizen the
                           tags. `pre-wrap` in .chat-bubble-body keeps the line
                           breaks the adjudicator typed. -->
                      <div class="chat-bubble-body">{{ item.message.body }}</div>
                      <!-- Inside the message, not in a separate list: the file and the
                           sentence explaining it are one thing. -->
                      <ul
                        v-if="item.message.attachments && item.message.attachments.length"
                        class="list-unstyled mb-0 mt-2 d-flex flex-column gap-1"
                      >
                        <li v-for="file in item.message.attachments" :key="file.id">
                          <i class="ri-attachment-2 align-bottom me-1"></i>
                          <a
                            v-if="file.temporary_url"
                            :href="file.temporary_url"
                            target="_blank"
                            rel="noopener"
                            >{{ file.original_name }}</a
                          >
                          <span v-else class="text-danger">
                            {{ file.original_name }} (unavailable)
                          </span>
                        </li>
                      </ul>
                      <div class="meta-text mt-1">
                        {{ formatted(item.message.created_at) }}
                        <span
                          v-if="item.message.author_type === 'adjudicator' && !item.message.pushed_at"
                          class="text-warning"
                          title="Processing to the citizen's portal"
                        >
                          · processing
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <div v-if="isOpen">
                <hr />
                <div class="mb-2">
                  <label class="form-label small" for="reply-body">Reply</label>
                  <textarea
                    id="reply-body"
                    class="form-control"
                    rows="4"
                    :maxlength="REPLY_MAX"
                    placeholder="Write to the appellant…"
                    v-model="reply.body"
                  ></textarea>
                  <p class="meta-text mb-0">
                    {{ reply.body.length }} / {{ REPLY_MAX }} characters — the appellant
                    reads this on their portal and in an email, both as plain text.
                  </p>
                </div>
                <div class="form-check mb-2">
                  <input
                    id="request-documents"
                    class="form-check-input"
                    type="checkbox"
                    v-model="reply.request_documents"
                  />
                  <label class="form-check-label small" for="request-documents">
                    This is a request for more documents (moves the contest to
                    “awaiting documents”)
                  </label>
                </div>
                <div class="mb-2">
                  <label class="form-label small" for="reply-files">
                    Attach documents (optional)
                  </label>
                  <input
                    id="reply-files"
                    class="form-control form-control-sm"
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.pdf"
                    @change="onFilesPicked"
                  />
                  <p class="text-muted small mb-0">
                    PNG, JPG or PDF, up to 2 MB each — the same limits the
                    citizen's portal accepts. They arrive with this message.
                  </p>
                </div>
                <button
                  class="btn btn-primary btn-sm"
                  type="button"
                  :disabled="!reply.body.trim()"
                  @click="submitReply"
                >
                  <i class="ri-send-plane-2-line align-bottom me-1"></i>Send<span
                    v-if="reply.files.length"
                  >
                    with {{ reply.files.length }} file(s)</span>
                </button>
              </div>
            </b-card>

            <!-- Read-only. The contest resolves itself once every contested NOV
                 has an outcome — all denied closes it as denied, otherwise as
                 resolved — so there is no decision to take here. A manual form
                 alongside that could only disagree with the NOVs it summarises. -->
            <div
              v-if="!isOpen"
              class="card mb-3 border-0"
              :class="
                ticket.resolution === 'denied' ? 'bg-soft-danger' : 'bg-soft-success'
              "
            >
              <div class="card-body">
                <h6 class="text-muted text-uppercase small fw-semibold mb-2">
                  <i class="ri-gavel-line align-bottom me-1"></i>Decision
                </h6>
                <p class="mb-1">
                  <strong>{{ ticket.resolution }}</strong> —
                  {{ formatted(ticket.resolved_at) }}
                </p>
                <p class="mb-1 text-muted small" v-if="ticket.adjudicator">
                  by {{ ticket.adjudicator.name }}
                </p>
                <p class="mb-0" style="white-space: pre-wrap">
                  {{ ticket.resolution_notes }}
                </p>
              </div>
            </div>

            <b-card v-else class="mb-3">
              <h6 class="text-muted text-uppercase small fw-semibold mb-2">
                <i class="ri-gavel-line align-bottom me-1"></i>Decision
              </h6>
              <p class="text-muted mb-0 small">
                This contest closes on its own once every NOV above has been
                decided. Decide each one with Amend, Void or Deny.
              </p>
            </b-card>
          </div>
        </div>
      </div>
    </b-overlay>

    <!-- The modals live OUTSIDE b-overlay on purpose.
         BOverlay renders its slot into a <span>, and every BModal is a
         <Teleport to="body">. Sitting them next to `<div v-if="ticket">` inside
         that span gave Vue a child list whose anchors are in document.body, so
         the first patch that moved anything — the ticket arriving, the overlay
         transition leaving — threw
           NotFoundError: Failed to execute 'insertBefore' on 'Node'
         and aborted the DOM update half-applied, which is why the spinner
         stayed up with `loading` already false.
         They teleport to body regardless, so nesting them bought nothing — and
         a modal should not be dimmed by the page's own loading overlay. -->

      <!-- amend modal -->
      <b-modal
        v-model="amend.open"
        header-bg-variant="warning"
        title-class="text-black"
        header-class="adj-modal-header"
        hide-footer
      >
        <template #title>
          <i class="ri-pencil-line align-bottom me-1"></i>Amend {{ amend.nov_no }}
        </template>
        <div class="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3">
          <i class="ri-error-warning-line fs-16 flex-shrink-0"></i>
          <span>This updates the traffic record and notifies the appellant. It can't be undone.</span>
        </div>
        <div class="alert alert-light border d-flex justify-content-between align-items-center py-2 mb-3">
          <span class="meta-text">Current amount</span>
          <span class="fw-semibold">{{ peso(amend.current_amount) }}</span>
        </div>
        <div class="mb-2">
          <label class="form-label small">New amount</label>
          <div class="input-group">
            <span class="input-group-text">₱</span>
            <input class="form-control" type="number" step="0.01" v-model="amend.amount" />
          </div>
          <p v-if="amendDelta" class="meta-text mb-0 mt-1">
            <i
              class="align-bottom me-1"
              :class="amendDelta.up ? 'ri-arrow-up-line text-danger' : 'ri-arrow-down-line text-success'"
            ></i>
            <span :class="amendDelta.up ? 'text-danger' : 'text-success'">
              {{ amendDelta.up ? "Up" : "Down" }} {{ peso(Math.abs(amendDelta.change)) }}
            </span>
            from the current amount
          </p>
        </div>
        <div class="mb-3">
          <label class="form-label small">Supporting document (required)</label>
          <input
            class="form-control"
            type="file"
            accept=".png,.jpg,.jpeg"
            @change="amend.attachment = $event.target.files[0]"
          />
        </div>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="busy || !amend.amount || !amend.attachment"
          @click="submitAmend"
        >
          <i class="ri-pencil-line align-bottom me-1"></i>Amend amount
        </button>
      </b-modal>

      <!-- void modal -->
      <b-modal
        v-model="voiding.open"
        header-bg-variant="secondary"
        title-class="text-white"
        header-close-white
        header-class="adj-modal-header"
        hide-footer
      >
        <template #title>
          <i class="ri-forbid-line align-bottom me-1"></i>
          Void the record for {{ voiding.nov_no }}
        </template>
        <div class="alert alert-dark d-flex align-items-center gap-2 py-2 mb-3">
          <i class="ri-error-warning-line fs-16 flex-shrink-0"></i>
          <span>
            This cancels the notice for good — nothing more will be due, and it
            lifts the LTO alarm. It can't be undone.
          </span>
        </div>
        <div class="mb-2">
          <label class="form-label small">Lift reason</label>
          <div class="input-group">
            <span class="input-group-text"><i class="ri-list-check-2"></i></span>
            <select class="form-select" v-model="voiding.lift_reason_id">
              <option value="">Choose a reason…</option>
              <option v-for="reason in liftReasons" :key="reason.id" :value="reason.id">
                {{ reason.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="mb-2">
          <label class="form-label small">Note</label>
          <div class="input-group">
            <span class="input-group-text"><i class="ri-sticky-note-line"></i></span>
            <textarea class="form-control" rows="3" v-model="voiding.note"></textarea>
          </div>
        </div>
        <div class="mb-2">
          <label class="form-label small">Supporting document (required)</label>
          <input
            class="form-control"
            type="file"
            accept=".png,.jpg,.jpeg,.pdf"
            @change="voiding.attachment = $event.target.files[0]"
          />
        </div>
        <button
          class="btn btn-danger"
          type="button"
          :disabled="
            busy || !voiding.note || !voiding.lift_reason_id || !voiding.attachment
          "
          @click="submitVoid"
        >
          <i class="ri-forbid-line align-bottom me-1"></i>Void record
        </button>
      </b-modal>

      <!-- deny modal -->
      <b-modal
        v-model="denying.open"
        header-bg-variant="danger"
        title-class="text-white"
        header-close-white
        header-class="adj-modal-header"
        hide-footer
      >
        <template #title>
          <i class="ri-close-circle-line align-bottom me-1"></i>Deny {{ denying.nov_no }}
        </template>
        <div class="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3">
          <i class="ri-error-warning-line fs-16 flex-shrink-0"></i>
          <span>The notice stands as issued. This can't be changed afterwards.</span>
        </div>
        <div class="mb-3">
          <label class="form-label small">Reason for Denial?</label>
          <textarea
            class="form-control"
            rows="3"
            placeholder="This is sent to the appellant."
            v-model="denying.reason"
          ></textarea>
        </div>
        <button
          class="btn btn-danger"
          type="button"
          :disabled="!denying.reason.trim()"
          @click="submitDeny"
        >
          <i class="ri-close-circle-line align-bottom me-1"></i>Deny this NOV
        </button>
      </b-modal>

      <!-- cancel hearing modal -->
      <b-modal
        v-model="cancellingHearing.open"
        header-bg-variant="danger"
        title-class="text-white"
        header-close-white
        header-class="adj-modal-header"
        hide-footer
      >
        <template #title>
          <i class="ri-close-circle-line align-bottom me-1"></i>Cancel the hearing
        </template>
        <div class="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3">
          <i class="ri-error-warning-line fs-16 flex-shrink-0"></i>
          <span>The contest goes back to under review, and the appellant is told.</span>
        </div>
        <div class="mb-3">
          <label class="form-label small">Why is it being cancelled?</label>
          <textarea
            class="form-control"
            rows="3"
            placeholder="This is sent to the appellant."
            v-model="cancellingHearing.reason"
          ></textarea>
        </div>
        <button
          class="btn btn-danger"
          type="button"
          :disabled="!cancellingHearing.reason.trim()"
          @click="submitCancelHearing"
        >
          <i class="ri-close-circle-line align-bottom me-1"></i>Cancel hearing
        </button>
      </b-modal>
  </Layout>
</template>

<style scoped>
.nov-card {
  transition: box-shadow 0.15s ease-in-out;
}

.nov-card:hover {
  box-shadow: 0 1px 2px rgba(56, 65, 74, 0.15);
}

/* Documents rows: a real thumbnail stands in for the icon on image
   attachments (a valid ID or an OR is easier to recognize as a photo than as
   a filename), so the adjudicator can tell what was submitted without
   opening every file in a new tab. */
.doc-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem;
  border: 1px solid var(--vz-border-color, #dee2e6);
  border-radius: 0.375rem;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out;
}

a.doc-row:hover {
  border-color: var(--vz-primary, #405189);
  box-shadow: 0 1px 2px rgba(56, 65, 74, 0.15);
}

.doc-row-disabled {
  cursor: default;
  opacity: 0.75;
}

.doc-thumb {
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 0.25rem;
  background-color: var(--vz-light, #f3f6f9);
  font-size: 1.25rem;
  color: var(--vz-primary, #405189);
}

.doc-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.doc-slot {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--vz-primary, #405189);
}

.chat-bubble {
  max-width: 85%;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  background-color: var(--vz-light, #f3f6f9);
  color: var(--vz-body-color, #212529);
}

.chat-bubble-body {
  white-space: pre-wrap;
  font-size: 0.9375rem;
  line-height: 1.55;
}

.chat-bubble-mine {
  background-color: rgba(var(--vz-primary-rgb, 64, 81, 137), 0.16);
  border-top-right-radius: 0.125rem;
}

.chat-bubble-theirs {
  border-top-left-radius: 0.125rem;
}

/* "The contest" card: the narration and appellant details were set in the
   theme's small, low-contrast muted gray, which reads as barely-there body
   copy. These read at full body size/color, with the narration additionally
   set apart as a distinct note so it's the first thing scanned, not a run-on
   paragraph blending into the card. */
.contest-narration {
  white-space: pre-wrap;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: var(--vz-body-color, #212529);
  background-color: var(--vz-light, #f3f6f9);
  border-left: 3px solid var(--vz-primary, #405189);
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
}

.meta-text {
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--vz-heading-color, #495057);
}

/* The hearing date stub — modelled on a printed hearing notice, where the
   date lives on its own tab rather than inside a sentence. Tinted with the
   primary color at low opacity rather than a flat fill, so it reads as an
   accent in both themes instead of a solid block competing with the card. */
.hearing-date-block {
  width: 4.25rem;
  padding: 0.5rem 0.25rem;
  border-radius: 0.5rem;
  background-color: rgba(var(--vz-primary-rgb, 64, 81, 137), 0.12);
  border: 1px solid rgba(var(--vz-primary-rgb, 64, 81, 137), 0.25);
}

.hearing-date-month {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--vz-primary, #405189);
}

.hearing-date-day {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--vz-body-color, #212529);
}

.hearing-date-weekday {
  font-size: 0.6875rem;
  text-transform: uppercase;
  color: var(--vz-heading-color, #495057);
}

.hearing-remarks {
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--vz-body-color, #212529);
  background-color: var(--vz-light, #f3f6f9);
  border-radius: 0.25rem;
  padding: 0.5rem 0.75rem;
}
</style>

<!-- Unscoped on purpose: <b-modal> teleports its markup to <body>, so there's
     no scoped ancestor in the real DOM for a scoped :deep() rule to attach
     to — reaching .modal-header here requires a plain global class passed in
     through the header-class prop. -->
<style>
/* This theme's modal styling zeroes out the header's bottom padding
   (.modal-dialog:not(.modal-dialog-scrollable) .modal-header) so the header
   hugs the body with no divider. With align-items: center that pulls the
   title and the close button down toward the bottom edge of the header
   instead of sitting centered in it — most visible with a tinted header
   banner. Restoring even padding puts the × back where it visually belongs:
   centered in its own banner, not crowding the content below it. */
.adj-modal-header {
  padding-bottom: 1.25rem !important;
  align-items: center !important;
}

.adj-modal-header .modal-title {
  display: flex;
  align-items: center;
  line-height: 1.2;
}
</style>
